// PRC-048: textos del panel «Gestión del aviso» (diseño 33, apartado 6). Los
// textos son los del diseño: no cambiarlos sin cambiar el diseño.

export const CAUSE_REASONS = [
  'Sustitución por otra referencia',
  'Pedido puntual (no recurrente)',
  'Referencia descatalogada',
  'Pérdida frente a la competencia',
  'Cliente inactivo o perdido',
  'Otro motivo',
] as const;

export const SUBSTITUTION_REASON = CAUSE_REASONS[0];

export const ACTIONS_TAKEN = [
  'Contacto con el cliente',
  'Llamada sin respuesta',
  'Visita comercial',
  'Sin actuación previa',
] as const;

/** Motivo de apertura cuando «Actuación realizada» queda vacía (PRC-048). */
export const NO_PRIOR_ACTION = 'Sin actuación previa';

export const NEXT_ACTIONS = [
  'Verificar próxima compra',
  'Nuevo contacto',
  'Revisión de precio',
  'Preparar propuesta',
  'Otra acción',
] as const;

export const OTHER_ACTION = 'Otra acción';

export type ReviewShortcut = 'tomorrow' | '1w' | '2w' | '1m' | 'other';

export const REVIEW_SHORTCUTS: Array<{ id: ReviewShortcut; label: string }> = [
  { id: 'tomorrow', label: 'Mañana' },
  { id: '1w', label: '1 semana' },
  { id: '2w', label: '2 semanas' },
  { id: '1m', label: '1 mes' },
  { id: 'other', label: 'Otra fecha' },
];

export type CadenceResolutionChoice = 'cause' | 'commercial' | 'not_relevant';

export const RESOLUTIONS: Array<{ id: CadenceResolutionChoice; title: string; hint: string }> = [
  { id: 'cause', title: 'Causa identificada', hint: 'Se archiva con su motivo' },
  { id: 'commercial', title: 'En gestión comercial', hint: 'Se abre un seguimiento' },
  { id: 'not_relevant', title: 'Sin relevancia comercial', hint: 'Se descarta' },
];

export const NOT_RELEVANT_TYPE = 'Sin relevancia comercial';
export const NOT_RELEVANT_CONCLUSION = 'Descartado: sin relevancia comercial';

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const pad = (n: number) => String(n).padStart(2, '0');

/** Fecha de solo día (aaaa-mm-dd) en local. */
export const toDateOnly = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** «dd/mm/aaaa» a partir de «aaaa-mm-dd» o de una fecha ISO. */
export const formatDayDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return '—';
  return `${m[3]}/${m[2]}/${m[1]}`;
};

/** «agosto de 2026». */
export const formatMonthYear = (value: string | null | undefined): string | null => {
  const m = value ? /^(\d{4})-(\d{2})/.exec(value) : null;
  return m ? `${MONTHS[Number(m[2]) - 1]} de ${m[1]}` : null;
};

export const reviewDateFor = (shortcut: ReviewShortcut, today = new Date()): string | null => {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (shortcut === 'tomorrow') d.setDate(d.getDate() + 1);
  else if (shortcut === '1w') d.setDate(d.getDate() + 7);
  else if (shortcut === '2w') d.setDate(d.getDate() + 14);
  else if (shortcut === '1m') d.setMonth(d.getMonth() + 1);
  else return null;
  return toDateOnly(d);
};

/** Fecha de revisión (aaaa-mm-dd) a las 09:00 locales, en ISO, para el servidor. */
export const reviewDateToIso = (dateOnly: string): string => {
  const [y, m, d] = dateOnly.split('-').map(Number);
  return new Date(y, m - 1, d, 9, 0, 0).toISOString();
};

/** «cada 30 días» (frecuencia habitual; en pantalla nunca «P90»). */
export const usualFrequencyLabel = (p90: number | null | undefined): string =>
  p90 == null || !Number.isFinite(Number(p90)) ? '—' : `cada ${Math.round(Number(p90))} días`;

/** Conclusión generada a partir de la selección (editable). */
export const buildCauseConclusion = (
  reason: string,
  productId: string,
  substitute: { product_id: string; product_label: string; since?: string | null } | null,
): string => {
  if (reason === SUBSTITUTION_REASON && substitute) {
    const label = substitute.product_label && substitute.product_label !== substitute.product_id
      ? ` · ${substitute.product_label}`
      : '';
    const since = formatMonthYear(substitute.since ?? null);
    return `Sustitución de ${productId} por ${substitute.product_id}${label}${since ? ` desde ${since}` : ''}.`;
  }
  return `${reason}.`;
};

/** Motivo de apertura del seguimiento: actuación (o «Sin actuación previa») y observaciones. */
export const buildOpeningReason = (actionTaken: string | null, notes: string): string => {
  const base = actionTaken || NO_PRIOR_ACTION;
  const extra = notes.trim();
  return extra ? `${base}: ${extra}` : base;
};

/** Ventas de 12 meses: sin compras en 12 meses son 0 €, no «sin dato». */
export const sales12mValue = (a: { sales_12m: number | null; purchase_days_12m: number }): number | null =>
  a.purchase_days_12m === 0 ? 0 : a.sales_12m;

/** Margen % de 12 meses: «—» sin ventas; «No calculable» si falta el coste. */
export const margin12mLabel = (
  a: { margin_pct_12m: number | null; purchase_days_12m: number },
  formatPct: (v: number) => string,
): string => (a.purchase_days_12m === 0 ? '—' : a.margin_pct_12m == null ? 'No calculable' : formatPct(a.margin_pct_12m));
